import FilterDropdown from './FilterDropdown'
import ViewPreviousHistoryModal from './ViewPreviousHistoryModal'
import useTranslations from '../../../hooks/useTranslations'
import ANTDButton from '../../../shared/antd/ANTDButton'
import { ANTDDateRange } from '../../../shared/antd/ANTDDatePicker'
import ANTDForm, { ANTDFormItem } from '../../../shared/antd/ANTDForm'
import ANTDInput, { ANTDTextArea } from '../../../shared/antd/ANTDInput'
import ANTDModal from '../../../shared/antd/ANTDModal'
import ANTDSelect from '../../../shared/antd/ANTDSelect'
import ANTDTable, {
  ANTDTableCell,
  ANTDTableRow,
} from '../../../shared/antd/ANTDTable'
import Label from '../../../shared/Label'
import { numberFormatting } from '../../../utils/customFunctions'
import { SAVE_FORMAT } from '../../../utils/dayjs'
import { isEqual, length } from '../../../utils/javascript'
import createReport from '../container/createReport.container'
import { filterKeys } from '../report.description'

const CreateReport = ({ editData }) => {
  const { t } = useTranslations()
  const {
    form,
    modal,
    columnList,
    columnFilters,
    equalNotEqual,
    summaryOptions,
    selectedFilters,
    filterDropdownItems,
    viewPreviousHistory,
    flexibleReportData,
    flexibleReportCalculation,
    setModal,
    onSaveReport,
    handleTableChange,
    onSelectionChange,
    onInputChange,
    onEqualNotEqualChange,
    handleDateRangeChange,
    setViewPreviousHistory,
    handleColumnFilterChange,
    onGenerateReport,
    calculationTag,
    setCalculationTag,
    onRemoveDateFilter,
  } = createReport({ editData })

  const PAGE_SIZE = 10
  const { list = [], pageNo, loader, fullCount } = flexibleReportData || {}
  const selectedColumnList = length(columnFilters)
    ? columnFilters
        .map(columnKey => columnList.find(({ key }) => isEqual(key, columnKey)))
        .filter(Boolean)
    : columnList

  const summaryDropdown = columnKeyId => (
    <ANTDSelect
      className="mt-5"
      style={{ width: 100 }}
      options={summaryOptions?.map(({ label, value }) => ({
        label: t(label),
        value,
      }))}
      defaultValue={summaryOptions[0].value}
      onChange={val =>
        setCalculationTag(prev => ({ ...prev, [columnKeyId]: val }))
      }
      value={calculationTag?.[columnKeyId]}
    />
  )

  return (
    <>
      <div className="d-flex justify-content-end">
        <ANTDButton type="primary" className="ml-10" onClick={onGenerateReport}>
          {t('dash_GenerateReport')}
        </ANTDButton>
        <ANTDButton
          type="primary"
          className="ml-10"
          onClick={() => setViewPreviousHistory({ open: true })}
        >
          {t('btn_ViewPreviousHistory')}
        </ANTDButton>
      </div>

      <div className="d-flex">
        <div className="filter-dropdown-container">
          {filterDropdownItems.map(({ key, type, menuProps, id, ...item }) =>
            type === 'dateRangePicker' ? (
              <div key={key} className="relative">
                <Label text={item.label} />
                <button
                  className="ant-btn remove-btn-icon cursor-pointer"
                  title="remove"
                  type="button"
                  onClick={() => onRemoveDateFilter(key)}
                />
                <ANTDDateRange
                  className
                  format={SAVE_FORMAT}
                  value={selectedFilters[key]}
                  onChange={dates => handleDateRangeChange({ key, dates })}
                />
              </div>
            ) : type === 'input' ? (
              <div key={key}>
                <ANTDInput
                  placeholder={item.placeholder}
                  value={selectedFilters[key]}
                  onChange={e =>
                    onInputChange({ key, value: e?.target?.value || '' })
                  }
                />
              </div>
            ) : (
              <FilterDropdown
                key={key}
                {...item}
                filterKey={key}
                equalNotEqual={equalNotEqual?.[key]}
                onEqualNotEqualChange={onEqualNotEqualChange}
                menuProps={{
                  ...menuProps,
                  id,
                  selectedKeys: selectedFilters[key],
                  onSelect: ({ selectedKeys }) => {
                    onSelectionChange({
                      key,
                      selectedKeys,
                      type: 'select',
                    })
                  },
                  onDeselect: ({ selectedKeys }) => {
                    onSelectionChange({
                      key,
                      selectedKeys,
                      type: 'deselect',
                    })
                  },
                }}
                mode={
                  isEqual(key, filterKeys.jobCompleted) ||
                  isEqual(key, filterKeys.rcAvailability)
                    ? 'single'
                    : 'multiple'
                }
              />
            ),
          )}
        </div>
        <ANTDButton
          type="link"
          className="mt-20"
          onClick={() => setModal({ open: true })}
        >
          {t('rpt_SaveReport')}
        </ANTDButton>
      </div>

      <Label text={t('job_ColumnFilter')} />
      <ANTDSelect
        className="w-100 mt-5"
        mode="multiple"
        maxTagCount="responsive"
        value={columnFilters}
        options={[
          { label: t('txt_All'), value: 'txt_All' },
          ...columnList.map(({ key, title }) => ({
            label: t(title),
            value: key,
          })),
        ]}
        onChange={handleColumnFilterChange}
      />

      <ANTDTable
        className="mt-15"
        tableLayout="fixed"
        columns={selectedColumnList?.map(col => ({
          ...col,
          title: t(col?.title),
        }))}
        dataSource={list}
        scroll={{ x: 'max-content' }}
        onChange={handleTableChange}
        loading={loader}
        pagination={{
          lastFetched: pageNo,
          current: pageNo,
          pageSize: PAGE_SIZE,
          total: fullCount,
          responsive: true,
          showTotal: (total, range) =>
            `${range[0]}-${range[1]} of ${total} items`,
        }}
        summary={rowData => {
          return (
            <ANTDTableRow>
              {selectedColumnList.map(col => {
                const columnTitleKey = col?.dataIndex
                const getKey =
                  typeof columnTitleKey === 'string'
                    ? columnTitleKey?.charAt(0)?.toUpperCase() +
                      columnTitleKey?.slice(1)
                    : ''
                const valueKey = isEqual(calculationTag?.[col.key], 'count')
                  ? `${getKey?.slice(0, 1)?.toLowerCase()}${getKey?.slice(
                      1,
                    )}NotNullCount`
                  : `${calculationTag?.[col.key] || 'sum'}${getKey}`
                return (
                  <ANTDTableCell className="numeric-cell" key={col.key}>
                    {col.isNumeric ? (
                      <>
                        {summaryDropdown(col.key)}
                        <span className="ml-10">
                          {numberFormatting(
                            flexibleReportCalculation[valueKey],
                          ) || '-'}
                        </span>
                      </>
                    ) : (
                      '-'
                    )}
                  </ANTDTableCell>
                )
              })}
            </ANTDTableRow>
          )
        }}
      />

      <ANTDModal
        open={modal.open}
        title={t('rpt_SaveReport')}
        onCancel={() => setModal({ open: false })}
        footer={false}
        width={1000}
        centered
        destroyOnClose
      >
        <ANTDForm layout="vertical" form={form} onFinish={onSaveReport}>
          <ANTDFormItem
            name={['name']}
            label={t('rpt_ReportName')}
            className="m-0 w-100 mt-5"
          >
            <ANTDInput />
          </ANTDFormItem>
          <ANTDFormItem
            name={['description']}
            label={t('inv_Description')}
            className="m-0 w-100 mt-5"
          >
            <ANTDTextArea />
          </ANTDFormItem>

          <div className="d-flex justify-center mt-10">
            <ANTDButton type="primary" htmlType="submit">
              {t('btn_Save')}
            </ANTDButton>
          </div>
        </ANTDForm>
      </ANTDModal>
      <ViewPreviousHistoryModal
        open={viewPreviousHistory.open}
        onClose={() => setViewPreviousHistory({ open: false })}
        payload={{
          sheetType: 'FLEXIBLE_REPORT',
        }}
        hideStartDate
        hideEndDate
      />
    </>
  )
}

export default CreateReport
